import {
  Box,
  Link,
  Stack,
  MenuItem,
  Typography,
  ListItemIcon,
  ListItemText,
} from "@mui/material";
import { TbShirtSport } from "react-icons/tb";
import { HiOutlineTicket } from "react-icons/hi";
import { OpenInNewRounded } from "@mui/icons-material";
import { MdOutlineEventSeat, MdOutlinedFlag } from "react-icons/md";

import { routes } from "@/global/routes";
import { throttleTime } from "@/global/utils/utils";
import { useTeamConfig } from "@/global/hooks/useTeamConfig";
import { useGenericModal } from "@/shared/common/GenericModal";
import { useRouterExtended } from "@/global/hooks/useRouterExtended";
import { useCssMediaQuery } from "@/shared/base/hooks/useCssMediaQuery";

import { TransferTicketButton } from "@/shared/modules/ticketTransfer/TransferTicketButton";
import { ModalActionButton } from "@/shared/base/components/ModalActionButton/ModalActionButton";

export const howItWorks = [
  {
    icon: <MdOutlinedFlag />,
    title: "Choose Your Games ",
    description: "Select the games you wish to attend.",
  },
  {
    icon: <TbShirtSport />,
    title: "Login to Account Manager",
    description: (
      <>
        Click Continue and connect to your{" "}
        <Link
          href={"https://am.ticketmaster.com/corockies/"}
          target={"_blank"}
          sx={{
            color: "primary.main",
            textDecoration: "none",
            fontWeight: 500,
          }}
        >
          Colorado Rockies
        </Link>{" "}
        account using our secure Ticketmaster login.
      </>
    ),
  },
  {
    icon: <MdOutlineEventSeat />,
    title: "Select Your Seats",
    description: "Select the seats you wish to purchase for the games.",
  },
  {
    icon: <HiOutlineTicket />,
    title: "Get Your Tickets",
    description: (
      <>
        Tickets will be available in your{" "}
        <Link
          href={"https://am.ticketmaster.com/corockies/"}
          target={"_blank"}
          sx={{
            color: "primary.main",
            textDecoration: "none",
            fontWeight: 500,
          }}
        >
          Colorado Rockies
        </Link>{" "}
        Account Manager within 24 hours of redemption. Reach out to Live Fan
        Support for any questions.
      </>
    ),
  },
];

export const faqs = [
  {
    title: "What are the benefits of bringing a group to the ballpark?",
    description: (
      <>
        Share the excitement of Colorado Rockies Baseball with a group of
        friends, family, coworkers, or classmates at the ballpark. When ordering
        through the Group Sales Department, groups of 20 or more will receive
        seating together (subject to availability), scoreboard recognition and 2
        (or 4) complimentary tickets to a future Rockies game for the group
        leader. Special group pricing is available for most home games.
        <br />
        <br />
        The bigger the group, the bigger the rewards! Groups of 300 or more can
        qualify for a pregame on-field ceremony and groups of 500 or more can
        qualify for an opportunity to watch batting practice on the field for
        the group leader and a guest.
        <br />
        <br />
        Fundraising opportunities are available for most home games with larger
        discounts available for designated Standard Fundraiser dates.
        <br />
        <br />
        Add concession value to your game ticket with PowerTickets and save $2
        per ticket. Power is available in $10, $15, $20, or $25 increments.
        <br />
        <br />
        Call{" "}
        <Link
          href="tel:3037625437"
          target="_blank"
          sx={{
            color: "primary.main",
            textDecoration: "none",
            fontWeight: 500,
          }}
        >
          303-ROCKIES (762-5437)
        </Link>{" "}
        for more information or to have a group brochure sent to you.
      </>
    ),
  },
  {
    title:
      "What is the minimum number of tickets required for a group purchase?",
    description: "20 tickets.",
  },
  {
    title: "What time can I get into the ballpark?",
    description:
      "Guests are encouraged to come early and watch batting practice, infield workouts and pregame ceremonies. Gates A (Rockpile entrance) and E will open two hours prior to game time for guests who want to view batting practice. Guests will be required to stay in the Left Field Pavilion area until the remaining ballpark gates open. Gates B, C and D will open 1½ hours prior to game time. Gate opening times may vary if game is rescheduled.",
  },
  {
    title: "Where is the best place to park my car, RV, or bus?",
    description: (
      <>
        The Coors Field parking lot is located at Park Avenue West and Wazee
        Street, in Denver's historic Lower Downtown. Whether you choose to
        drive, take RTD, bike or walk, planning ahead can help make your visit
        to the ballpark more enjoyable.
        <br /> Single game parking passes are available in a Colorado Rockies
        operated lot for $20-$25 in Lot A and $18-$23 in Lot B (credit cards
        ONLY). Parking pass prices are subject to change. Passes may be
        purchased at the Coors Field Ticket Office, Colorado Rockies Dugout
        stores, online at{" "}
        <Link
          href="http://www.rockies.com/parking"
          target="_blank"
          sx={{
            color: "primary.main",
            textDecoration: "none",
            fontWeight: 500,
          }}
        >
          ROCKIES.com/parking
        </Link>{" "}
        or by calling{" "}
        <Link
          href="tel:3037625437"
          target="_blank"
          sx={{
            color: "primary.main",
            textDecoration: "none",
            fontWeight: 500,
          }}
        >
          303-ROCKIES (762-5437)
        </Link>
        . Bus parking is $40 per vehicle. (Service charges may apply, prices are
        subject to change).
      </>
    ),
  },
  {
    title: "What methods of transportation are available to the ballpark?",
    description: (
      <>
        Light Rail is a fast and efficient means of transportation to Coors
        Field. There are three Light Rail stations convenient to the ballpark;
        the most convenient stop is Union Station via the C Line. Light Rail
        runs daily. For specific route and fare information, access the Light
        Rail section of the RTD website found at{" "}
        <Link
          href="https://www.rtd-denver.com"
          target="_blank"
          sx={{
            color: "primary.main",
            textDecoration: "none",
            fontWeight: 500,
          }}
        >
          rtd-denver.com
        </Link>{" "}
        or call{" "}
        <Link
          href="tel:3032996000"
          target="_blank"
          sx={{
            color: "primary.main",
            textDecoration: "none",
            fontWeight: 500,
          }}
        >
          (303) 299-6000
        </Link>
        .
      </>
    ),
  },
  {
    title: "What other pre-game options are available to groups?",
    description: (
      <>
        There are four picnic areas at Coors Field available for pregame
        parties.
        <br />
        <br />
        The <b>COORS OUTFIELD PICNIC AREA</b>, an outdoor area located adjacent
        to Gate A, is a great place for groups of 50 to 2,000 to gather and
        socialize prior to game time for great food and fun.
        <br />
        <br />
        The <b>PLATTE RIVER PICNIC AREA</b> is located inside the ballpark under
        cover of the Rockpile seating area and is equipped with picnic tables
        and chairs to accommodate groups of 20 to 250.
        <br />
        <br />
        The <b>JACK DANIEL'S Pre-Game Patio</b> is located on the upper level of
        The Rooftop and accommodates groups of 80 to 300.
        <br />
        <br />
        The <b>CLUB LEVEL PARTY PACK</b> is a private buffet on the Club Level
        for groups of 80 to 300.
        <br />
        <br />
        Reservations in these areas include a full buffet with non-alcoholic
        beverages and special game ticket discounts. For more information,
        please call{" "}
        <Link
          href="tel:3037625437"
          target="_blank"
          sx={{
            color: "primary.main",
            textDecoration: "none",
            fontWeight: 500,
          }}
        >
          303-ROCKIES (762-5437)
        </Link>
        . All four areas are accessible to guests with disabilities.
      </>
    ),
  },
];

export const RegistrationTerms = () => (
  <>
    In connection with your registration, you will receive ticket-related
    commercial emails from{" "}
    <Link
      target={"_blank"}
      sx={{
        color: "primary.main",
        textDecoration: "none",
        fontWeight: 500,
      }}
      href="https://www.mlb.com/rockies"
    >
      rockies.com
    </Link>
    . You may unsubscribe at any time. By checking this box you agree to receive
    additional commercial emails from{" "}
    <Link
      target={"_blank"}
      sx={{
        fontWeight: 500,
        color: "primary.main",
        textDecoration: "none",
      }}
      href="https://www.mlb.com/rockies"
    >
      rockies.com
    </Link>{" "}
    and{" "}
    <Link
      target={"_blank"}
      sx={{
        fontWeight: 500,
        color: "primary.main",
        textDecoration: "none",
      }}
      href="https://www.mlb.com"
    >
      MLB.com
    </Link>
    . The terms and conditions of your ticket and policies applicable to its use
    are updated to address circumstances related to COVID-19 and health and
    safety. Further, due to the COVID-19 pandemic, scheduling may be impacted;
    games may be canceled, rescheduled, or postponed or games may be held with
    limited capacity. Upon completing your purchase by selecting Purchase
    Tickets you acknowledge and agree that your credit card will be charged. By
    clicking “Purchase Tickets”, you agree to the{" "}
    <Link
      target={"_blank"}
      sx={{
        fontWeight: 500,
        color: "primary.main",
        textDecoration: "none",
      }}
      href="https://www.mlb.com/app/ballpark/official-information/privacy-policy"
    >
      MLB Privacy Policy
    </Link>
    ,{" "}
    <Link
      target={"_blank"}
      sx={{
        color: "primary.main",
        textDecoration: "none",
        fontWeight: 500,
      }}
      href="https://www.mlb.com/app/ballpark/official-information/terms-of-use"
    >
      Terms of Use
    </Link>{" "}
    and to receive your ticket(s) via an SMS text message to the mobile #
    associated with your Rockies account. Message and data rates may apply
    Tickets will be made available in the MLB Ballpark mobile application only.{" "}
    <Link
      target={"_blank"}
      sx={{
        fontWeight: 500,
        color: "primary.main",
        textDecoration: "none",
      }}
      href="https://www.mlb.com"
    >
      MLB.com
    </Link>{" "}
    and supported mobile device required.
  </>
);

export const CheckoutTerms = () => (
  <>
    In connection with your purchase, you will receive ticket-related commercial
    emails from{" "}
    <Link
      target="_blank"
      sx={{
        fontWeight: 500,
        color: "primary.main",
        textDecoration: "none",
      }}
      href="https://www.mlb.com/rockies"
    >
      rockies.com
    </Link>
    . You may unsubscribe at any time. By checking this box you agree to receive
    additional commercial emails from{" "}
    <Link
      target="_blank"
      sx={{
        fontWeight: 500,
        color: "primary.main",
        textDecoration: "none",
      }}
      href="https://www.mlb.com/rockies"
    >
      rockies.com
    </Link>{" "}
    and{" "}
    <Link
      target="_blank"
      sx={{
        fontWeight: 500,
        color: "primary.main",
        textDecoration: "none",
      }}
      href="https://www.mlb.com/"
    >
      MLB.com
    </Link>
    . The terms and conditions of your ticket and policies applicable to its use
    are updated to address circumstances related to COVID-19 and health and
    safety. Further, due to the COVID-19 pandemic, scheduling may be impacted;
    games may be canceled, rescheduled, or postponed or games may be held with
    limited capacity. Upon completing your purchase by selecting Purchase
    Tickets you acknowledge and agree that your credit card will be charged. By
    clicking “Purchase Tickets”, you agree to the{" "}
    <Link
      target="_blank"
      sx={{
        fontWeight: 500,
        color: "primary.main",
        textDecoration: "none",
      }}
      href="https://www.mlb.com/app/ballpark/official-information/privacy-policy"
    >
      MLB Privacy Policy
    </Link>
    ,{" "}
    <Link
      target="_blank"
      sx={{
        fontWeight: 500,
        color: "primary.main",
        textDecoration: "none",
      }}
      href="https://www.mlb.com/app/ballpark/official-information/terms-of-use"
    >
      Terms of Use
    </Link>{" "}
    and to receive your ticket(s) via an SMS text message to the mobile #
    associated with your Rockies account. Message and data rates may apply
    Tickets will be made available in the MLB Ballpark mobile application only.{" "}
    <Link
      target="_blank"
      sx={{
        fontWeight: 500,
        color: "primary.main",
        textDecoration: "none",
      }}
      href="https://www.mlb.com/"
    >
      MLB.com
    </Link>{" "}
    and supported mobile device required.
  </>
);

export const RockiesMenuItems = () => (
  <Box>
    <MenuItem
      onClick={() => {
        window.open(
          "https://www.mlb.com/rockies/schedule/printable-schedule",
          "_blank",
        );
      }}
    >
      <ListItemIcon>
        <OpenInNewRounded fontSize="small" />
      </ListItemIcon>
      <ListItemText>Full Schedule</ListItemText>
    </MenuItem>
  </Box>
);

export function CheckoutSuccessDescription() {
  const teamConfig = useTeamConfig();
  const { md: mdUp } = useCssMediaQuery("up");
  const router = useRouterExtended();
  const viewTicketsModal = useGenericModal();

  const onClickViewTicket = () => {
    viewTicketsModal.set({
      isOpen: true,
      title: <Box height="32px"></Box>,
      hideTitleIcon: true,
      description: (
        <Box>
          <Typography variant="body1" sx={{ mb: 3 }}>
            By clicking View Tickets, you will be redirected to the Colorado
            Rockies Account Manager to access your barcodes. Please note that
            any ticket transfers or forwarding completed there will not be
            reflected in this portal. For a seamless experience, please manage
            all transfers and forwarding through the Transfer Tickets portal.
          </Typography>
        </Box>
      ),

      closeIconProps: {
        onClick: () => {
          viewTicketsModal.set({ isOpen: false });
          throttleTime(400, () => {
            viewTicketsModal.reset();
          });
        },
      },

      closeButtonText: "View Tickets",
      closeButtonProps: {
        variant: "outlined",
      },
      onClickClose: () => {
        window.location.href = teamConfig.accountManagerUrl;
      },

      confirmButtonText: "Transfer Tickets",
      onClickConfirm: async () => {
        await router.push({
          pathname: routes.ticket_transfer,
          query: {
            ...router.query,
            eventCode: router?.query?.eventCode as string,
            orderNumber: Number(router?.query?.orderNumber),
          },
        });
      },
    });
  };

  return (
    <Box>
      <Typography fontSize={"1.1rem"} color={"text.primary"} lineHeight={1.4}>
        Your order was successful! If you&apos;d like to transfer tickets to
        members of your group. Click Transfer Tickets to get started.
      </Typography>
      <Stack
        direction={mdUp ? "row" : "column"}
        justifyContent={"center"}
        alignItems="center"
        spacing={2}
        mt={2.5}
      >
        <TransferTicketButton
          size="large"
          eventCode={router?.query?.eventCode as string}
          orderNumber={Number(router?.query?.orderNumber)}
          sx={{
            width: mdUp ? "inherit" : "100%",
          }}
        />

        <ModalActionButton
          variant="outlined"
          onClick={onClickViewTicket}
          size="large"
          sx={{
            width: mdUp ? "170px" : "100%",
          }}
        >
          View Tickets
        </ModalActionButton>
      </Stack>
    </Box>
  );
}
